import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  PermissionsResponse,
  ComponentsResponse,
  ComponentPermission,
  UpdatePermissionsPayload
} from '../models/permission-models';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  private permissionsSubject = new BehaviorSubject<ComponentPermission[]>([]);
  public permissions$ = this.permissionsSubject.asObservable();

  private isSuperAdminSubject = new BehaviorSubject<boolean>(false);
  public isSuperAdmin$ = this.isSuperAdminSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(private http: HttpClient) {
    this.loadPermissionsFromStorage();
  }

  getComponents(): Observable<ComponentsResponse> {
    return this.http.get<ComponentsResponse>(`${environment.apiURL}/admin/permissions/components`);
  }

  getMyPermissions(): Observable<PermissionsResponse> {
    this.loadingSubject.next(true);
    return this.http.get<PermissionsResponse>(`${environment.apiURL}/admin/permissions/me`);
  }

  getUserPermissions(userId: string[]): Observable<PermissionsResponse> {
    let params = new HttpParams();
    userId.forEach(id => { params = params.append('userId', id); });
    return this.http.get<PermissionsResponse>(`${environment.apiURL}/admin/permissions/user`, { params });
  }

  updateUserPermissions(payload: UpdatePermissionsPayload): Observable<any> {
    return this.http.put<any>(`${environment.apiURL}/admin/permissions/user`, payload);
  }

  hasPermission(componentName: string, permission: 'read' | 'write' | 'edit' | 'delete'): boolean {
    const isSuperAdmin = this.isSuperAdminSubject.value;
    if (isSuperAdmin) {
      return true;
    }

    const permissions = this.permissionsSubject.value;
    const backendName = componentName;
    const component = permissions.find(p => p.componentName === backendName);

    if (!component) {
      return false;
    }

    const compPerms = component.permissions;

    switch (permission) {
      case 'read':
        return compPerms.read || compPerms.write || compPerms.edit || compPerms.delete;

      case 'write':
        return compPerms.write || compPerms.edit || compPerms.delete;

      case 'edit':
        return compPerms.edit || compPerms.delete;

      case 'delete':
        return compPerms.delete;

      default:
        return false;
    }
  }

  canRead(componentName: string): boolean {
    return this.hasPermission(componentName, 'read');
  }

  canWrite(componentName: string): boolean {
    return this.hasPermission(componentName, 'write');
  }

  canEdit(componentName: string): boolean {
    return this.hasPermission(componentName, 'edit');
  }

  canDelete(componentName: string): boolean {
    return this.hasPermission(componentName, 'delete');
  }

  setPermissions(permissions: ComponentPermission[], isSuperAdmin: boolean = false): void {
    this.permissionsSubject.next(permissions);
    this.isSuperAdminSubject.next(isSuperAdmin);
    this.savePermissionsToStorage(permissions, isSuperAdmin);
  }

  refreshPermissions(): Observable<PermissionsResponse> {
    this.loadingSubject.next(true);
    const observable = this.getMyPermissions();
    observable.subscribe({
      next: (response) => {
        if (response.success) {
          this.setPermissions(response.permissions || [], response.isSuperAdmin || false);
        }
        this.loadingSubject.next(false);
      },
      error: () => {
        this.loadingSubject.next(false);
      }
    });
    return observable;
  }

  clearPermissions(): void {
    this.permissionsSubject.next([]);
    this.isSuperAdminSubject.next(false);
    localStorage.removeItem('permissions');
    localStorage.removeItem('isSuperAdmin');
  }

  getPermissions(): ComponentPermission[] {
    return this.permissionsSubject.value;
  }

  getIsSuperAdmin(): boolean {
    return this.isSuperAdminSubject.value;
  }

  private savePermissionsToStorage(permissions: ComponentPermission[], isSuperAdmin: boolean): void {
    localStorage.setItem('permissions', JSON.stringify(permissions));
    localStorage.setItem('isSuperAdmin', String(isSuperAdmin));
  }

  private loadPermissionsFromStorage(): void {
    const storedPermissions = localStorage.getItem('permissions');
    const storedIsSuperAdmin = localStorage.getItem('isSuperAdmin');

    if (storedPermissions) {
      try {
        const permissions = JSON.parse(storedPermissions);
        const isSuperAdmin = storedIsSuperAdmin === 'true';
        this.permissionsSubject.next(permissions);
        this.isSuperAdminSubject.next(isSuperAdmin);
      } catch (error) {
        console.error('Error loading permissions from storage:', error);
      }
    }
  }
}

